import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SponsorsShowcase from '../components/SponsorsShowcase';
import BackButton from '../components/BackButton';
import { Shield, FileText, Eye, Users, ArrowRight, Target, Award, Network, Globe, Heart, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(true);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const FOGEDOMPage = () => {
  const [heroRef, heroInView] = useInView();
  const [missionRef, missionInView] = useInView();
  const [processRef, processInView] = useInView();
  const [valuesRef, valuesInView] = useInView();

  const missions = [
    { icon: FileText, title: 'Instruction technique', desc: 'Analyse approfondie et vérification de chaque dossier selon des critères rigoureux.', gradient: 'from-blue-500 to-cyan-500' },
    { icon: Eye, title: 'Diagnostic terrain', desc: 'Vérification sur site et évaluation des situations réelles.', gradient: 'from-green-500 to-emerald-500' },
    { icon: Users, title: 'Instruction sociale', desc: 'Prise en compte du contexte social et territorial de chaque demande.', gradient: 'from-purple-500 to-pink-500' },
    { icon: Award, title: 'Rapport d\'instruction', desc: 'Transmission du rapport pour examen selon les règles du fonds interne FOGEDOM-SCIC.', gradient: 'from-amber-500 to-orange-500' },
  ];

  const steps = [
    { num: '01', title: 'Réception du dossier', desc: 'Le dossier transmis par COPPAM est réceptionné et enregistré.' },
    { num: '02', title: 'Analyse documentaire', desc: 'Vérification de la complétude et de la conformité des pièces.' },
    { num: '03', title: 'Diagnostic terrain', desc: 'Visite et expertise sur site si nécessaire.' },
    { num: '04', title: 'Rédaction du rapport', desc: 'Synthèse et recommandations transmises au Fonds.' },
  ];

  const values = [
    { icon: Shield, title: 'Rigueur', desc: 'Analyse méthodique et objective de chaque dossier' },
    { icon: Heart, title: 'Humanité', desc: 'Prise en compte des situations individuelles' },
    { icon: Globe, title: 'Territorialité', desc: 'Connaissance approfondie des Outre-mer' },
    { icon: Zap, title: 'Réactivité', desc: 'Traitement diligent des demandes' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80" 
            alt="Document analysis and expertise" 
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-900/90 to-slate-950" />
        </div>
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-red-600/20 to-orange-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-red-500/20 to-orange-500/20 text-red-300 border border-red-500/30 mb-6 px-4 py-2">
              <FileText className="w-4 h-4 mr-2" /> Organisme d'instruction
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-red-100 to-white bg-clip-text text-transparent">F.O.G.E.D.O.M</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-4 leading-relaxed font-semibold">
              Partenaire associatif d’instruction
            </p>
            
            <p className="text-lg text-slate-400 max-w-3xl mb-10 leading-relaxed">
              F.O.G.E.D.O.M intervient dans l’<span className="text-red-400">instruction sociale, territoriale et technique</span>{' '}
              des demandes. Partenaire distinct d’O’SCOP, il conserve ses responsabilités propres.
              Ses relations avec la SCIC sont organisées par convention.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-red-500/25 hover:shadow-red-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/fogedom/presentation'}>
                Découvrir FOGEDOM-SCIC <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section ref={missionRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${missionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-red-500/20 text-red-300 border border-red-500/30 mb-4 px-4 py-2"><Target className="w-4 h-4 mr-2" /> Nos missions</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">L'instruction au cœur du <span className="text-red-400">dispositif</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {missions.map((mission, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${missionInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${mission.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <mission.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{mission.title}</h3>
                <p className="text-slate-400 leading-relaxed">{mission.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section ref={processRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-red-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30 mb-4 px-4 py-2"><Network className="w-4 h-4 mr-2" /> Processus</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Parcours d'<span className="text-blue-400">instruction</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div key={index} className={`relative bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300 ${processInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="text-4xl font-black text-red-500/30 mb-4">{step.num}</div>
                <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                <p className="text-sm text-slate-400">{step.desc}</p>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-red-500 to-transparent" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section ref={valuesRef} className="py-24 bg-gradient-to-br from-red-600 via-orange-600 to-red-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Nos <span className="text-red-200">valeurs</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className={`bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 text-center ${valuesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 mx-auto rounded-2xl bg-white/20 flex items-center justify-center mb-4">
                  <value.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                <p className="text-sm text-white/70">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-950 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Découvrir le <span className="text-amber-400">Fonds FOGEDOM-SCIC</span></h2>
          <p className="text-xl text-slate-400 mb-10">Le fonds interne de soutien et de prévention d’OBJECTIF SCOP OUTREMER, sans personnalité juridique distincte.</p>
          <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-10 py-6 text-lg shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/fogedom/presentation'}>
            FOGEDOM-SCIC <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <SponsorsShowcase variant="branch" branche="FOGEDOM" />
      <Footer />
    </div>
  );
};

export default FOGEDOMPage;
